import { useState, useEffect, useCallback } from 'react';
import { useMutation } from 'react-query';
import { useClient } from 'hooks/useClient';
import { useSession } from 'store/session';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { expirationName } from 'config/constants';
import { getCurrentRole } from 'utils/getCurrentRole';
import { getLegacyToken } from 'helpers/sessionContext';
import { getStorage, setLegacyToken, setSessionUser } from 'helpers/sessionContext';



const acceptInvitationMutation = (client: any, id: string) => {
  return client.acceptInvitation(id);
};

const rejectInvitationMutation = (client: any, id: string) => {
  return client.declineInvitation(id);
};

const renewLoginMutation = async (client: any) => {
  const tokenV1 = getLegacyToken();
  if (tokenV1) return client.renewLogin(tokenV1);
  return Promise.resolve(null);
};

export interface Invitation {
  id: string;
  sender: {
    email: string;
    name?: string;
  };
  organization?: {
    name: string;
  };
}

export const useInvitations = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [invitations, setInvitations] = useState<Invitation[]>([]);

  const client = useClient({
    url: process.env.REACT_APP_API,
    token: getLegacyToken() as string,
  });

  const {
    defaultOrganization,
    setOrganizations,
    setOrganizationImages,
    setUser,
    setToken: setSessionToken,
    setIsLoggedIn,
    isLoggedIn,
    user,
  } = useSession();

  const { mutateAsync: acceptInvitationMutate, isLoading: isLoadingAccept } =
    useMutation((id: string) => acceptInvitationMutation(client, id));

  const { mutateAsync: rejectInvitationMutate, isLoading: isLoadingReject } =
    useMutation((id: string) => rejectInvitationMutation(client, id));

  const { mutateAsync: renewLogin } = useMutation(() =>
    renewLoginMutation(client),
  );

  const refreshUserData = useCallback(async () => {
    const renewResponse: any = await renewLogin();

    if (renewResponse?.user) {
      setLegacyToken(renewResponse.token);
      getStorage().setItem(expirationName, renewResponse.expiration);
      setSessionUser(renewResponse.user);
      setSessionToken(renewResponse.token);
      setUser(renewResponse.user);
      setIsLoggedIn(true);

      const formatedOrganizations: any = [];
      const organizationImages: any = [];

      for (const i in renewResponse.user.organizations) {
        organizationImages.push({
          id: renewResponse.user.organizations[i].id,
          image: renewResponse.user.organizations[i].imagotype,
        });

        const customOrg = {
          ...renewResponse.user.organizations[i],
          imagotype: null,
          rol: getCurrentRole(renewResponse.user.organizations[i].permissions),
        };
        formatedOrganizations.push(customOrg);
      }

      const orgCincelCopy = {
        ...defaultOrganization,
        rol: false,
      };

      setOrganizations([orgCincelCopy, ...formatedOrganizations]);
      setOrganizationImages(organizationImages);
    }
  }, [
    renewLogin,
    setSessionToken,
    setUser,
    setIsLoggedIn,
    setOrganizations,
    setOrganizationImages,
    defaultOrganization,
  ]);

  // Initial load to refresh user data
  useEffect(() => {
    if (user && isLoggedIn) {
      refreshUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get invitations from user data
  useEffect(() => {
    if (user?.invitations) {
      setInvitations(user.invitations);
    }
  }, [user]);

  const handleAcceptInvitation = async (id: string) => {
    const acceptResponse: any = await acceptInvitationMutate(id);

    if (!acceptResponse.ok && acceptResponse.errors) {
      const error = Object.keys(acceptResponse.errors)
        .map((key) => acceptResponse.errors[key][0])
        .join(',');

      toast({
        position: 'top-right',
        isClosable: true,
        title: error,
        status: 'error',
      });
      return false;
    }

    if (acceptResponse.ok) {
      toast({
        position: 'top-right',
        isClosable: true,
        title: t(translations.USER_DROPDOWN.INVITATION_ACCEPTED),
        status: 'success',
      });

      // Remove from local state
      setInvitations((prev) => prev.filter((inv) => inv.id !== id));

      // Refresh user data to get updated organizations
      await refreshUserData();
      return true;
    }

    return false;
  };

  const handleRejectInvitation = async (id: string) => {
    const rejectResponse: any = await rejectInvitationMutate(id);

    if (!rejectResponse.ok && rejectResponse.errors) {
      const error = Object.keys(rejectResponse.errors)
        .map((key) => rejectResponse.errors[key][0])
        .join(',');

      toast({
        position: 'top-right',
        isClosable: true,
        title: error,
        status: 'error',
      });
      return false;
    }

    if (rejectResponse.ok) {
      toast({
        position: 'top-right',
        isClosable: true,
        title: t(translations.USER_DROPDOWN.INVITATION_REJECTED),
        status: 'success',
      });

      // Remove from local state
      setInvitations((prev) => prev.filter((inv) => inv.id !== id));
      return true;
    }

    return false;
  };

  return {
    invitations,
    invitationsCount: invitations.length,
    isLoading: isLoadingAccept || isLoadingReject,
    handleAcceptInvitation,
    handleRejectInvitation,
  };
};
